import React, { useEffect, useState } from 'react';

import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import UploadIcon from '@mui/icons-material/CloudUploadOutlined';

import InteractiveFilePreview from './InteractiveFilePreview/InteractiveFilePreview';
import {
  checkIsExcelFile,
  checkIsImage,
  checkIsPdfFile,
  checkIsPowerpointFile,
  checkIsVideo,
  checkIsWordFile,
} from '../../../utils/file-utils';
import { ReactComponent as WordIcon } from '../../../assets/icons/file-preview/word.svg';
import { ReactComponent as ExcelIcon } from '../../../assets/icons/file-preview/excel.svg';
import { ReactComponent as PowerpointIcon } from '../../../assets/icons/file-preview/powerpoint.svg';
import FileIcon from '@mui/icons-material/Description';
import PdfIcon from '@mui/icons-material/PictureAsPdf';
import { timeAgo } from '../../../utils/date-time-utils';
import { useStyles } from './AppFile.styles';
import theme from '../../../styles/theme';
import { INewFile } from '../../../types/interfaces/new-file';
import { IAppFile } from '../../../types/interfaces/app-file';
import { useLazyGetDownloadURL } from '../../../services/redux/api/fireBaseStorageApi';

type File = IAppFile | INewFile;

interface AppFileProps {
  file: File;
  onDelete?: (file: File) => void;
  onUpload?: (file: File) => void;
  onClick?: () => void;
}

export default function AppFile(props: AppFileProps) {
  const { file, onDelete, onUpload, onClick = () => {} } = props;
  const [fileSrc, setFileSrc] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [getFileUrl] = useLazyGetDownloadURL();

  const { classes } = useStyles(theme);

  useEffect(() => {
    if ('fileBlob' in file) {
      setFileSrc(file.fileBlob);
    } else if ('storageLink' in file) {
      setIsLoading(true);
      getFileUrl(file.previewStorageLink || file.storageLink)
        .then((response) => setFileSrc(response.data))
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [file]);

  const getImageWrapper = (component: JSX.Element) => {
    return <div className={classes.imageWrapper}>{component}</div>;
  };

  function getFilePreview() {
    if (checkIsImage(file.mimeType)) {
      return <InteractiveFilePreview fileSrc={fileSrc} alt="Img preview" />;
    }

    if (checkIsVideo(file.mimeType)) {
      return (
        <InteractiveFilePreview
          fileSrc={fileSrc}
          type="video"
          alt="Video preview"
        />
      );
    }

    if (checkIsWordFile(file.mimeType)) {
      return getImageWrapper(<WordIcon />);
    }

    if (checkIsExcelFile(file.mimeType)) {
      return getImageWrapper(<ExcelIcon />);
    }

    if (checkIsPowerpointFile(file.mimeType)) {
      return getImageWrapper(<PowerpointIcon />);
    }

    if (checkIsPdfFile(file.mimeType)) {
      return getImageWrapper(<PdfIcon />);
    }

    return getImageWrapper(<FileIcon />);
  }

  return (
    <div className={classes.fileContainer} onClick={onClick}>
      <div className={classes.fileWrapper}>
        <div className={classes.img}>
          {isLoading ? (
            <div className={classes.loader}>
              <CircularProgress color="primary" />
            </div>
          ) : (
            <>
              {getFilePreview()}
              {/*<div className={classes.footer}>*/}
              {/*  <div className={classes.fileName}>{file.name}</div>*/}
              {/*  <div className={classes.createdAt}>*/}
              {/*    {timeAgo(file.createdAt)}*/}
              {/*  </div>*/}
              {/*</div>*/}
            </>
          )}
        </div>
      </div>
      {(onDelete || onUpload) && (
        <div className={classes.buttonsContainer}>
          {onUpload && (
            <UploadIcon
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onUpload(file);
              }}
            />
          )}
          {onDelete && (
            <CloseIcon
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onDelete(file);
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}
